import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs'
import { ActionTreeNode } from '../models/actions-tree-node.model'
import { JmeleonActionsForRightService } from './jmeleon-actions-for-right.service'
import { tap, map, withLatestFrom, take } from 'rxjs/operators'
import { TreeNode, SelectItem } from 'primeng/api'
import { NotificationService } from '../../../services/notification.service'
import JMeleonActionTreeUtils from '../utils/jml-action-tree.utils'
import { LocalBackendMiscDataStorageService } from '../../../services/local-backend-misc-data-storage.service'
import { JMeleonTreeTableTransformUtils } from '../utils/jml-treetable-transform.utils'
import { TreeTableData, TreeTableSection, TreeTableEntityNode } from '../models/actions-tree-node.model'

/**
 * This facade encapsulates backend-calls and business logic for the right-action-editor
 */
@Injectable({
  providedIn: 'root'
})
export class JmeleonActionsFacadeService {
  treeTableData$: Observable<TreeTableData>
  treeTableSections$: Observable<TreeTableSection[]>
  actionsTree$: Observable<ActionTreeNode>
  selectedTreeNodes$: Observable<TreeNode[]>
  sections$: Observable<SelectItem[]>
  isLoading$: Observable<boolean>
  disableEditing$: Observable<boolean>
  masterOfDisasterId$: Observable<number>
  currentRightId$: Observable<number>

  private $treeTableData = new BehaviorSubject<TreeTableData>({ sections: [], totalSelectedActions: 0 })
  private $treeTableSections = new BehaviorSubject<TreeTableSection[]>([])

  // dict with tuple: [tree-data, selected-nodes]
  private _sectionDict: Record<string, [TreeNode[], TreeNode[]]>
  private _rightId: number
  private _actionTreeRoot: ActionTreeNode
  private _currentSection: string
  private _lastSelectedSectionKey: string | null = null
  private $actionTree = new BehaviorSubject<ActionTreeNode>(null)
  private $selectedTreeNodes = new BehaviorSubject<TreeNode[]>([])
  private $actionGuiTreeForSelectedSection = new BehaviorSubject<TreeNode[]>([])
  private $sections = new BehaviorSubject<SelectItem[]>([])
  private $isLoading = new BehaviorSubject<boolean>(false)
  private $masterOfDisasterId = new BehaviorSubject<number>(0)
  private $currentRightId = new BehaviorSubject<number>(0)

  private subscriptions: Subscription[] = []

  constructor(
    private jmlActionsForRightService: JmeleonActionsForRightService,
    private notificationService: NotificationService,
    // private storageService: BrowserStorageService,
    // private settingsService: SettingsService
    private localBackendMiscDataStorage: LocalBackendMiscDataStorageService
  ) {
    this.treeTableData$ = this.$treeTableData.asObservable()
    this.treeTableSections$ = this.$treeTableSections.asObservable()

    this.actionsTree$ = this.$actionTree.asObservable()
    this.selectedTreeNodes$ = this.$selectedTreeNodes.asObservable()
    this.sections$ = this.$sections.asObservable()
    this.isLoading$ = this.$isLoading.asObservable()
    this.masterOfDisasterId$ = this.$masterOfDisasterId.asObservable()
    this.currentRightId$ = this.$currentRightId.asObservable()

    this.init()
  }

  /**
   * Initializes the actions facade service by performing setup tasks.
   *
   * @remarks
   * This method does the following:
   * - Checks for the master of disaster ID
   * - Transforms the action tree into a tree table format when available
   * - Updates sections based on the transformed tree table
   * - Sets up a reactive stream to disable editing when the current right ID matches the master of disaster ID
   */
  init(): void {
    this.checkForMasterOfDisasterId()
    this.actionsTree$
      .pipe(
        tap((actionTree) => {
          if (actionTree) {
            const treeTableData = JMeleonTreeTableTransformUtils.transformToTreeTable(actionTree)
            this.$treeTableData.next(treeTableData)
            this.$treeTableSections.next(treeTableData.sections)

            this.updateSections()
          }
        })
      )
      .subscribe()

    this.disableEditing$ = this.currentRightId$.pipe(
      withLatestFrom(this.masterOfDisasterId$),
      map((value) => value[0] === value[1])
    )
  }

  /**
   * Toggles an action or entity for a specific section in the tree table.
   * @param sectionName - The name of the section containing the action/entity
   * @param entityPath - The path of the entity associated with the action
   * @param actionName - The name of the action to toggle (null for entity toggle)
   * @param isSelected - Indicates whether the action/entity should be added (true) or removed (false)
   */
  public toggleTreeTableAction(
    sectionName: string,
    entityPath: string,
    actionName: string | null,
    isSelected: boolean
  ): void {
    const isEntityToggle: boolean = actionName === null
    const fullPath: string = isEntityToggle
      ? entityPath
        ? `${sectionName}.${entityPath}`
        : sectionName
      : entityPath
        ? `${sectionName}.${entityPath}.${actionName}`
        : `${sectionName}.${actionName}`

    const serviceCall = isSelected
      ? this.jmlActionsForRightService.addActionToRight(fullPath, this._rightId)
      : this.jmlActionsForRightService.removeActionFromRight(fullPath, this._rightId)

    this.subscriptions.push(
      serviceCall.subscribe({
        next: () => this.handleToggleSuccess(isEntityToggle, sectionName, entityPath, actionName, isSelected),
        error: () => this.handleToggleError(isSelected)
      })
    )
  }

  requestActionTreeFromBackend(rightId: number): void {
    this._rightId = rightId
    this.$currentRightId.next(rightId)
    this.$isLoading.next(true)

    this.subscriptions.push(
      this.jmlActionsForRightService.getActionsForRight(rightId).subscribe((actionTree) => {
        this.$actionTree.next(actionTree)
        this.$isLoading.next(false)
      })
    )
  }

  /**
   * Selects a section in the tree table data and updates the action GUI tree and selected tree nodes accordingly.
   *
   * @param sectionName - The name of the section to select. If null or empty, clears the action GUI tree and selected nodes.
   * @remarks
   * This method handles section selection by:
   * - Setting the current section
   * - Checking if the section exists in the tree table data (both current and future values)
   * - Updating the action GUI tree and selected tree nodes based on the selected section
   */
  selectSection(sectionName: string): void {
    this._currentSection = sectionName

    if (!sectionName) {
      this.$actionGuiTreeForSelectedSection.next(null)
      this.$selectedTreeNodes.next(null)
      return
    }

    this.onSelectSectionActions(sectionName)
  }

  /**
   * Tracks batch operations for adding or removing actions, storing metadata about each batch.
   *
   * @remarks
   * Maintains a map of batch operations with details such as total actions, completed actions,
   * operation type, and start time for tracking progress and performance.
   */
  private batchOperations = new Map<
    string,
    {
      total: number
      completed: number
      operation: 'add' | 'remove'
      startTime: number
    }
  >()

  /**
   * Executes an action operation (add or remove) for a specific right and action path.
   *
   * @remarks
   * Handles both individual and batch action operations with appropriate notifications.
   * Supports adding or removing actions from a right, with optional success callback.
   *
   * @param operation - The type of operation to perform ('add' or 'remove')
   * @param rightId - The identifier of the right to modify
   * @param actionPath - The full path of the action to add or remove
   * @param onSuccess - Optional callback function to execute on successful operation
   * @param batchId - Optional batch identifier for tracking batch operations
   */
  private executeActionOperation(
    operation: 'add' | 'remove',
    rightId: number,
    actionPath: string,
    onSuccess?: () => void,
    batchId?: string
  ): void {
    const serviceCall =
      operation === 'add'
        ? this.jmlActionsForRightService.addActionToRight(actionPath, rightId)
        : this.jmlActionsForRightService.removeActionFromRight(actionPath, rightId)

    this.subscriptions.push(
      serviceCall.subscribe({
        next: () => {
          onSuccess?.()

          // Handle batch notifications
          if (batchId) {
            this.handleBatchSuccess(batchId, operation)
          } else {
            // Single operation - show individual notification
            const successMessage =
              operation === 'add' ? 'Aktion wurde erfolgreich hinzugefügt.' : 'Aktion wurde erfolgreich entfernt.'
            this.notificationService.addSuccessNotification(successMessage)
          }

          this.updateSections()
        },
        error: (error) => {
          if (batchId) {
            this.handleBatchError(batchId, operation)
          } else {
            const errorMessage =
              operation === 'add' ? 'Fehler beim Hinzufügen der Aktion.' : 'Fehler beim Entfernen der Aktion.'
            this.notificationService.addErrorNotification(errorMessage)
          }
          console.error(`Action ${operation} failed:`, error)
        }
      })
    )
  }

  /**
   * Handles the successful completion of a batch operation.
   *
   * @param batchId - Unique identifier for the batch operation
   * @param operation - Type of operation performed ('add' or 'remove')
   * @remarks Tracks batch progress, updates completion status, and triggers a success notification when all operations are complete
   */
  private handleBatchSuccess(batchId: string, operation: 'add' | 'remove'): void {
    const batch = this.batchOperations.get(batchId)
    if (!batch) return

    batch.completed++

    if (batch.completed >= batch.total) {
      const actionWord = operation === 'add' ? 'hinzugefügt' : 'entfernt'
      const message = `${batch.total} Aktionen wurden erfolgreich ${actionWord}.`

      this.notificationService.addSuccessNotification(message)
      this.batchOperations.delete(batchId)
    }
  }

  /**
   * Handles errors that occur during a batch operation.
   *
   * @param batchId - Unique identifier for the batch operation
   * @param operation - Type of operation performed ('add' or 'remove')
   * @remarks Displays an error notification and removes the batch operation from tracking
   */
  private handleBatchError(batchId: string, operation: 'add' | 'remove'): void {
    const batch = this.batchOperations.get(batchId)
    if (!batch) return

    const actionWord = operation === 'add' ? 'Hinzufügen' : 'Entfernen'
    this.notificationService.addErrorNotification(
      `Fehler beim ${actionWord} von Aktionen. Einige Operationen sind möglicherweise fehlgeschlagen.`
    )

    this.batchOperations.delete(batchId)
  }

  /**
   * Executes a batch operation (add or remove) on multiple tree nodes for a specific right.
   *
   * @param operation - The type of operation to perform ('add' or 'remove')
   * @param rightId - The ID of the right to modify
   * @param nodes - Array of tree nodes to process in the batch operation
   * @param sectionNodes - Tree nodes representing the updated section
   * @remarks
   * Generates a unique batch ID, tracks the batch operation progress,
   * and executes individual action operations for each node.
   * Updates the section dictionary after each node operation.
   */
  executeBatchTreeNodeOperation(
    operation: 'add' | 'remove',
    rightId: number,
    nodes: TreeNode[],
    sectionNodes: TreeNode[]
  ): void {
    if (nodes.length === 0) return

    // Generate unique batch ID
    const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Register batch
    this.batchOperations.set(batchId, {
      total: nodes.length,
      completed: 0,
      operation,
      startTime: Date.now()
    })

    // Execute all operations with same batch ID
    nodes.forEach((node) => {
      const fullName = JMeleonActionTreeUtils.generateFullPathFromTreeNode(node, this._actionTreeRoot)

      this.executeActionOperation(
        operation,
        rightId,
        fullName,
        () => {
          // Update section dictionary for each node
          this._sectionDict[this._currentSection] = [this._sectionDict[this._currentSection][0], sectionNodes]
        },
        batchId
      )
    })
  }

  /**
   * Executes a batch operation (add or remove) on multiple specific actions for a given right.
   *
   * @param operation - The type of operation to perform ('add' or 'remove')
   * @param rightId - The ID of the right to modify
   * @param actionPaths - Array of action paths to process in the batch operation
   * @remarks
   * Generates a unique batch ID, tracks the batch operation progress,
   * and executes individual action operations for each specified action path.
   */
  executeBatchSpecificActionsOperation(operation: 'add' | 'remove', rightId: number, actionPaths: string[]): void {
    if (actionPaths.length === 0) return

    const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    this.batchOperations.set(batchId, {
      total: actionPaths.length,
      completed: 0,
      operation,
      startTime: Date.now()
    })

    actionPaths.forEach((actionPath) => {
      this.executeActionOperation(operation, rightId, actionPath, undefined, batchId)
    })
  }

  /**
   * Adds an action to a specific right by executing an 'add' operation for a given tree node.
   *
   * @param rightId - The ID of the right to which the action will be added
   * @param node - The tree node representing the action to be added
   * @param sectionNodes - An array of tree nodes associated with the current section
   * @remarks
   * Generates the full action path from the tree node, executes the action operation,
   * and updates the section dictionary with the provided section nodes.
   */
  addActionToRight(rightId: number, node: TreeNode, sectionNodes: TreeNode[]): void {
    const fullName = JMeleonActionTreeUtils.generateFullPathFromTreeNode(node, this._actionTreeRoot)

    this.executeActionOperation('add', rightId, fullName, () => {
      this._sectionDict[this._currentSection] = [this._sectionDict[this._currentSection][0], sectionNodes]
    })
  }

  /**
   * Removes an action from a specific right by executing a 'remove' operation for a given tree node.
   *
   * @param rightId - The ID of the right from which the action will be removed
   * @param node - The tree node representing the action to be removed
   * @param sectionNodes - An array of tree nodes associated with the current section
   * @remarks
   * Generates the full action path from the tree node, executes the action operation,
   * and updates the section dictionary with the provided section nodes.
   */
  removeActionFromRight(rightId: number, node: TreeNode, sectionNodes: TreeNode[]): void {
    const fullName = JMeleonActionTreeUtils.generateFullPathFromTreeNode(node, this._actionTreeRoot)

    this.executeActionOperation('remove', rightId, fullName, () => {
      this._sectionDict[this._currentSection] = [this._sectionDict[this._currentSection][0], sectionNodes]
    })
  }

  /**
   * Adds a specific action to a right by executing an 'add' operation for a given entity and action.
   *
   * @param rightId - The ID of the right to which the action will be added
   * @param entityName - The name of the entity associated with the action
   * @param actionName - The name of the specific action to be added
   * @remarks
   * Constructs a full action path by combining the entity and action names,
   * then executes the action operation to add the action to the specified right.
   */
  addSpecificActionToRight(rightId: number, entityName: string, actionName: string): void {
    const fullActionPath = `${entityName}.${actionName}`
    this.executeActionOperation('add', rightId, fullActionPath)
  }

  /**
   * Removes a specific action from a right by executing a 'remove' operation for a given entity and action.
   *
   * @param rightId - The ID of the right from which the action will be removed
   * @param entityName - The name of the entity associated with the action
   * @param actionName - The name of the specific action to be removed
   * @remarks
   * Constructs a full action path by combining the entity and action names,
   * then executes the action operation to remove the action from the specified right.
   */
  removeSpecificActionFromRight(rightId: number, entityName: string, actionName: string): void {
    const fullActionPath = `${entityName}.${actionName}`
    this.executeActionOperation('remove', rightId, fullActionPath)
  }

  checkForMasterOfDisasterId(): void {
    this.localBackendMiscDataStorage
      .getMasterOfDisasterId()
      .pipe(
        take(1),
        tap((modId) => this.$masterOfDisasterId.next(modId))
      )
      .subscribe()
  }

  public resetState(): void {
    this.$actionTree.next(null) // Clear for new backend request
    this.$selectedTreeNodes.next([]) // Clear current selections
    this.$actionGuiTreeForSelectedSection.next([]) // Clear current action tree
    this._sectionDict = {}
    this._actionTreeRoot = null
  }

  public setLastSelectedSectionKey(sectionKey: string): void {
    this._lastSelectedSectionKey = sectionKey
  }

  public getLastSelectedSectionKey(): string | null {
    return this._lastSelectedSectionKey
  }

  private updateTreeTableDataAfterAction(
    sectionName: string,
    entityPath: string,
    actionName: string,
    isSelected: boolean
  ): void {
    const currentData: TreeTableData = this.$treeTableData.value

    if (!currentData.sections) return

    const section: TreeTableSection = currentData.sections.find(
      (section: TreeTableSection) => section.name === sectionName
    )

    if (!section) return

    if (entityPath) {
      const pathParts: string[] = entityPath.split('.')
      let currentEntity: TreeTableEntityNode = this.findEntityByPath(section.entities, pathParts)

      if (currentEntity && currentEntity.actions.hasOwnProperty(actionName)) {
        currentEntity.actions[actionName] = isSelected
      }
    }

    this.updateSelectedCount(isSelected, section)
    this.updateTotalSelectedCount(isSelected, currentData)
    this.updateTreeTableData(currentData)
  }

  private updateTotalSelectedCount(isSelected: boolean, currentData: TreeTableData) {
    currentData.totalSelectedActions = isSelected
      ? (currentData.totalSelectedActions || 0) + 1
      : (currentData.totalSelectedActions || 0) - 1
  }

  private updateSelectedCount(isSelected: boolean, section: TreeTableSection) {
    section.selectedCount = isSelected ? (section.selectedCount || 0) + 1 : (section.selectedCount || 0) - 1
  }

  private updateTreeTableData(currentData: TreeTableData) {
    this.$treeTableData.next(currentData)
  }

  private handleToggleSuccess(
    isEntityToggle: boolean,
    sectionName: string,
    entityPath: string,
    actionName: string | null,
    isSelected: boolean
  ): void {
    this.showSuccessNotificationOnToggleTreeTableAction(isSelected)

    if (!isEntityToggle) {
      this.updateTreeTableDataAfterAction(sectionName, entityPath, actionName!, isSelected)
    }

    this.updateSections()
  }

  private handleToggleError(isSelected: boolean): void {
    this.showErrorNotificationOnToggleTreeTableAction(isSelected)
  }

  /**
   * Finds an entity by following the path parts recursively.
   */
  private findEntityByPath(entities: TreeTableEntityNode[], pathParts: string[]): TreeTableEntityNode | null {
    if (!entities || pathParts.length === 0) return null

    for (const entity of entities) {
      if (entity.name === pathParts[0]) {
        if (pathParts.length === 1) {
          return entity
        } else {
          return this.findEntityByPath(entity.children || [], pathParts.slice(1))
        }
      }
    }
    return null
  }

  private showSuccessNotificationOnToggleTreeTableAction(isSelected: boolean) {
    const message: string = isSelected ? 'Aktion wurde erfolgreich hinzugefügt.' : 'Aktion wurde erfolgreich entfernt.'
    this.notificationService.addSuccessNotification(message)
  }

  private showErrorNotificationOnToggleTreeTableAction(isSelected: boolean) {
    const message: string = isSelected ? 'Fehler beim Hinzufügen der Aktion.' : 'Fehler beim Entfernen der Aktion.'
    this.notificationService.addErrorNotification(message)
  }

  private updateSections = () => {
    // Get current TreeTable data
    const currentTreeTableData = this.$treeTableData.value

    if (!currentTreeTableData.sections || currentTreeTableData.sections.length === 0) {
      this.$sections.next([])
      return
    }

    // Transform TreeTable sections to SelectItem format for the UI
    const selectItems = currentTreeTableData.sections.map((section) => ({
      label: section.name,
      value: {
        key: section.name,
        hasSelectedNodes: (section.selectedCount || 0) > 0,
        selectedCount: section.selectedCount || 0
      }
    }))

    this.$sections.next(selectItems)
  }

  private onSelectSectionActions(sectionName: string) {
    this.subscriptions.push(
      this.treeTableData$
        .pipe(
          take(1),
          tap((currentTreeTableData) => {
            if (!currentTreeTableData?.sections) {
              this.$actionGuiTreeForSelectedSection.next(null)
              this.$selectedTreeNodes.next(null)
              return
            }

            const selectedSection: TreeTableSection = currentTreeTableData.sections.find(
              (section: TreeTableSection) => section.name === sectionName
            )

            if (!selectedSection) {
              this.$actionGuiTreeForSelectedSection.next(null)
              this.$selectedTreeNodes.next(null)
              return
            }

            this.$actionGuiTreeForSelectedSection.next([])
            this.$selectedTreeNodes.next([])
          })
        )
        .subscribe()
    )
  }
}
